/**
 * createBatchRuntime hook 에서 추출한 순수 helpers.
 *
 * hook 본체는 `@assistant-ui/react` + React state 의존이라 vitest node 환경에서
 * 직접 mount 가 불가하다. 본 파일은 테스트 가능한 순수 로직만 모아 hook 에서 import
 * 하여 사용하도록 한다 — coverage 에도 포함된다.
 */
import type { ChatMessage } from "../types.ts"
import { loadMessages } from "./persist.ts"

/**
 * fetcher 가 throw 한 에러를 사용자에게 보여줄 assistant 메시지로 변환한다.
 *
 * abort 인 경우는 호출 측이 미리 걸러야 하므로 본 함수는 일반 Error 만 가정한다.
 *
 * @param err fetcher 가 throw 한 값 (Error 권장, 문자열 등도 best-effort)
 * @returns text-part 한 개를 포함한 assistant ChatMessage
 */
export function buildErrorMessage(err: unknown): ChatMessage {
	const message = err instanceof Error ? err.message : String(err)
	return {
		role: "assistant",
		content: [
			{
				type: "text",
				text: `요청 처리 중 오류가 발생했습니다: ${message}`
			}
		]
	}
}

/**
 * 초기 messages 결정 — localStorage 에 저장된 메시지가 있으면 그것을 우선 사용,
 * 없으면 호출처가 제공한 initialMessages, 그것도 없으면 빈 배열.
 *
 * @param storageKey localStorage 키
 * @param initialMessages 호출처가 정의한 초기 메시지
 * @returns 사용해야 할 초기 메시지 배열
 */
export function pickInitialMessages(
	storageKey: string,
	initialMessages: ReadonlyArray<ChatMessage> | undefined
): ReadonlyArray<ChatMessage> {
	const saved = loadMessages(storageKey)
	if (saved.length > 0) return saved
	return initialMessages ?? []
}

/**
 * fetcher 가 정상 응답한 경우 다음 messages 를 만든다 (replies 가 있을 때만 append).
 *
 * @param prev 사용자 메시지가 이미 추가된 history
 * @param replies fetcher 가 반환한 assistant 응답 배열 (빈 배열 가능)
 * @returns 새 messages 배열 — replies 가 비면 prev 그대로
 */
export function appendReplies(
	prev: ReadonlyArray<ChatMessage>,
	replies: ReadonlyArray<ChatMessage>
): ReadonlyArray<ChatMessage> {
	if (replies.length === 0) return prev
	return [...prev, ...replies]
}
